import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, RefreshCw, PlusSquare, Sparkles, ArrowRight, CheckCircle, MapPin, ArrowRightLeft, Hammer, Calculator, Palette, ExternalLink, Wrench, Layers, Building, Paintbrush, Settings } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/sections/CTASection';
import JobTreadSection from '../components/sections/JobTreadSection';
import { serviceAreas } from '../data/mock';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const iconMap = {
  Home: Home,
  Wrench: Wrench,
  PlusSquare: PlusSquare,
  Layers: Layers,
  Building: Building,
  Paintbrush: Paintbrush,
  Hammer: Hammer,
  Settings: Settings,
  ArrowRightLeft: ArrowRightLeft,
  RefreshCw: RefreshCw,
};

const serviceFeatures = {
  "New Builds": [
    "Custom design services available",
    "We can build from your plans",
    "Interior design and material selections by Lilac Interiors",
    "Full project management from design to completion",
    "Warranty coverage and post-construction support"
  ],
  "Home Transformations": [
    "Complete whole-home renovations",
    "Modern updates and upgrades throughout",
    "Structural modifications and improvements",
    "Kitchen, bathroom, and living space redesign",
    "Interior design and material selections by Lilac Interiors"
  ],
  "Additions": [
    "Room additions and extensions",
    "Second-story additions",
    "Sunrooms and enclosed porches",
    "Garage conversions",
    "Seamless integration with existing structure"
  ],
  "Remodeling": [
    "Kitchen and bathroom renovations",
    "Room-specific transformations",
    "Modern upgrades and updates",
    "Custom finishes and fixtures",
    "Interior design and material selections by Lilac Interiors"
  ]
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/services/public`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const serviceColors = [
    { accent: 'bg-cyan-500', text: 'text-cyan-600' },
    { accent: 'bg-rose-500', text: 'text-rose-600' },
    { accent: 'bg-yellow-500', text: 'text-yellow-600' },
    { accent: 'bg-cyan-500', text: 'text-cyan-600' },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-yellow-400 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Hammer className="w-8 h-8 text-cyan-400" />
          </div>
          {/* Mobile Layout - Stacked */}
          <h1 className="text-4xl font-bold text-white mb-6 sm:hidden">
            <span className="block">Comprehensive</span>
            <span className="block my-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_c3admin-portal/artifacts/eo2e68tb_Design%2BBuild%20%28Wide%20-%20Dark%29.png" 
                alt="Design + Build" 
                className="h-[50px] inline-block"
                style={{ filter: 'brightness(0) saturate(100%) invert(76%) sepia(45%) saturate(1000%) hue-rotate(140deg) brightness(95%) contrast(90%)' }}
              />
            </span>
            <span className="block">Solutions</span>
          </h1>
          {/* Desktop Layout - Inline */}
          <h1 className="hidden sm:inline-flex sm:text-5xl lg:text-6xl font-bold text-white mb-6 items-end justify-center flex-wrap gap-x-4">
            <span>Comprehensive</span>
            <img 
              src="https://customer-assets.emergentagent.com/job_c3admin-portal/artifacts/eo2e68tb_Design%2BBuild%20%28Wide%20-%20Dark%29.png" 
              alt="Design + Build" 
              className="h-[90px] lg:h-[110px] -mb-5 lg:-mb-6"
              style={{ filter: 'brightness(0) saturate(100%) invert(76%) sepia(45%) saturate(1000%) hue-rotate(140deg) brightness(95%) contrast(90%)' }}
            />
            <span>Solutions</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto mt-8">
            From new construction to complete transformations, we deliver exceptional craftsmanship and personalized service on every project.
          </p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
            </div>
          ) : (
            services.map((service, index) => {
            const Icon = iconMap[service.icon] || Home;
            const isEven = index % 2 === 0;
            const features = serviceFeatures[service.title] || [];
            const color = serviceColors[index % 4];

            return (
              <div
                key={service.id}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 items-center mb-24 last:mb-0`}
              >
                {/* Image */}
                <div className="w-full lg:w-1/2">
                  <div className="relative group">
                    <div className={`absolute inset-0 ${color.accent} rounded-2xl transform rotate-3 group-hover:rotate-6 transition-transform`} />
                    <img
                      src={service.image}
                      alt={service.title}
                      className="relative w-full h-80 lg:h-96 object-cover rounded-2xl shadow-xl"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="w-full lg:w-1/2">
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 ${color.accent} rounded-xl flex items-center justify-center`}>
                      {Icon && <Icon className="w-7 h-7 text-white" />}
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">
                      {service.title}
                    </h2>
                  </div>
                  <p className="text-slate-600 text-lg leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className={`w-5 h-5 ${color.text} mt-0.5 flex-shrink-0`} />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/consultation"
                    className={`inline-flex items-center ${color.text} font-semibold group`}
                  >
                    Request Consultation
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            );
          }))}
        </div>
      </section>

      {/* Design Partnership Section */}
      <section id="lilac-interiors" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="flex flex-col lg:flex-row items-stretch">
              {/* Left side - Logo */}
              <div className="w-full lg:w-2/5 bg-gradient-to-br from-slate-100 to-slate-200 p-8 lg:p-12 flex items-center justify-center">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <img 
                    src="/images/lilac-interiors-logo.webp" 
                    alt="Lilac Interiors" 
                    className="h-40 lg:h-52 w-auto"
                  />
                </div>
              </div>
              
              {/* Right side - Content */}
              <div className="w-full lg:w-3/5 p-8 lg:p-12">
                <div className="flex items-center gap-3 mb-4">
                  <Palette className="w-8 h-8 text-cyan-600" />
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
                    Collaborative Interior Design Services
                  </h2>
                </div>
                <p className="text-slate-600 text-lg leading-relaxed mb-6">
                  When you hire C3 Contracting, you get a complete design-build experience. We've partnered with <strong className="text-cyan-600">Lilac Interiors</strong> to provide professional interior design and material selection services for our new build, home transformation, and major remodel clients.
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  Lilac Interiors is integrally woven into our team and collaborates closely with our clients through the design process, ensuring every detail reflects your personal style and vision. No need to coordinate with third-party designers or worry about anything falling through the cracks.
                </p>
                
                {/* Services List */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-8">
                  {[
                    "Plumbing Fixtures",
                    "Electrical Fixtures",
                    "Flooring",
                    "Tiling",
                    "Cabinet Layout & Styling",
                    "Cabinet Hardware",
                    "Countertops",
                    "Paint Colors",
                    "Door & Trim Styling",
                    "Door Hardware",
                    "Bath Accessories",
                    "Accents"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-500 italic mb-6">
                  <CheckCircle className="w-4 h-4 text-cyan-500 inline mr-2" />
                  Furniture & Décor Selection (available upon request)
                </p>
                
                <a
                  href="https://www.lilac-interiors.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Visit Website
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JobTread Section */}
      <JobTreadSection />

      {/* Project Estimator CTA */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-cyan-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start gap-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  Get an Instant Estimate
                </h2>
              </div>
              <p className="text-cyan-100 text-lg max-w-xl">
                Use our interactive Project Estimator to get a quick conceptual cost estimate for your project. No commitment required.
              </p>
            </div>
            <Link
              to="/estimator"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-cyan-600 hover:bg-cyan-50 font-semibold rounded-lg transition-colors group whitespace-nowrap"
            >
              Try Our Project Estimator
              <span className="ml-2 text-lg font-bold">$</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-6 h-6 text-cyan-600" />
              <h2 className="text-2xl font-bold text-slate-900">Service Areas</h2>
            </div>
            <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
              We proudly serve homeowners throughout the Charlotte metro and Lake Norman region, including:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {serviceAreas.map((area) => (
                <span 
                  key={area} 
                  className="bg-white text-slate-700 px-5 py-2 rounded-full shadow-sm border border-slate-200 text-sm font-medium"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
      <Footer />
    </div>
  );
};

export default ServicesPage;
